import {DataContentWrapper} from '@hconnect/common/containers'
import {CardBox, CardLinkWrapper} from '@hconnect/uikit/src/lib2'
import {Box, SxProps, Typography, Theme} from '@mui/material'
import {PlantStatusDailyKpis} from '@widgets/kpis'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router'

import {FEATURE_NAMESPACE} from '../consts'
import {useOptimizationSpecialistTimeRange, useOptimizationSpecialistKpis} from '../hooks'

const cardBoxPx = {xs: 2, md: 3}

type Props = {
  plantId: string
  cardBoxSx?: SxProps<Theme>
  placeholderWrapperSx?: SxProps<Theme>
  isMobile?: boolean
  gridItemXs: number
  optimizationSpecialistPerformanceHref?: string
}

export const OptimizationSpecialistKpisContainer: React.FC<Props> = ({
  plantId,
  cardBoxSx,
  placeholderWrapperSx,
  isMobile,
  gridItemXs,
  optimizationSpecialistPerformanceHref
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {start, end} = useOptimizationSpecialistTimeRange(plantId)

  const {
    data = [],
    isLoading,
    isError,
    isRefetching,
    refetch
  } = useOptimizationSpecialistKpis({plantId, from: start, to: end})

  const cardContent = (
    <CardBox
      data-test-id="plant-status-kpis-card"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...cardBoxSx,
        px: 0,
        pl: cardBoxPx
      }}
    >
      <Typography variant="h3" sx={{mb: 2}}>
        {t('plantStatus.cardTitle')}
      </Typography>
      <DataContentWrapper
        showLoader={isLoading}
        isError={isError}
        isRefetching={isRefetching}
        data={data}
        retryFunction={() => void refetch()}
        progressSx={{color: 'primary.main'}}
        placeholderWrapperSx={placeholderWrapperSx}
      >
        <Box sx={{flex: 1, overflowY: isMobile ? undefined : 'auto', pr: cardBoxPx}}>
          <PlantStatusDailyKpis kpiItems={data} isMobile={isMobile} gridItemXs={gridItemXs} />
        </Box>
      </DataContentWrapper>
    </CardBox>
  )

  if (!optimizationSpecialistPerformanceHref) {
    return cardContent
  }

  return (
    <CardLinkWrapper<typeof Link>
      component={Link}
      to={optimizationSpecialistPerformanceHref}
      data-test-id="plant-status-kpis-card-link"
    >
      {cardContent}
    </CardLinkWrapper>
  )
}
